import { AlertTriangle, OctagonX, RefreshCw, Sparkles } from 'lucide-react';

import { Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { ngayVN, soVN } from '../../../lib/dinh-dang';
import { maLoi } from '../../../lib/errors';
import { useAuthStore } from '../../../stores/auth';
import { useTenantCuaToi } from '../../auth/hooks/useTenantCuaToi';
import { VAI_TRO_QUAN_TRI_TENANT } from '../../auth/nghiep-vu/quyen-cong-tenant';
import { CachTinhHanMuc, DongHanMuc } from '../../tenant/components/ChiTietHanMucToken';
import { ThuTuDungToken } from '../../tenant/components/ThuTuDungToken';
import { TomTatTokenAi } from '../../tenant/components/TomTatTokenAi';
import { useHanMucTokenAi } from '../../tenant/hooks/useHanMucTokenAi';
import { mucToken } from '../../tenant/nghiep-vu/han-muc-token';
import { Tile, TileHeader } from './primitives';
import { YeuCauTokenToChuc } from './YeuCauTokenToChuc';

/**
 * Quản trị tổ chức: role lớp 1 `TENANT_ADMIN` khi đăng nhập thật; tài khoản mẫu không có role lớp 1
 * nên đọc theo nhãn vai trò của nó. API chỉ mở cho `TENANT_ADMIN` (ADR-0088 mục 3) — người dùng
 * thường không được thấy một ô luôn báo "không có quyền".
 */
function useLaQuanTriToChuc(): boolean {
  return useAuthStore((s) => {
    const u = s.user;
    if (!u) return false;
    return u.vaiTroLop1 ? u.vaiTroLop1.includes(VAI_TRO_QUAN_TRI_TENANT) : u.roles.includes('vaiTro.quanTriToChuc');
  });
}

/**
 * Ô "Token AI tháng này" ở Tổng quan của cổng tenant (ADR-0088): Tenant Admin xem mức dùng AI của cả tổ
 * chức, chỉ đọc. Từ 80% có dải cảnh báo, đã chặn có dải đỏ nói rõ cái gì dừng, tới bao giờ, và cái gì
 * vẫn chạy. `tenant_id` lấy từ ngữ cảnh đăng nhập (`useTenantCuaToi`), không bao giờ từ URL.
 *
 * Nhân sự nền tảng xem chéo (không có tenant) và người dùng thường không thấy ô này.
 */
export function TheTokenAiToChuc() {
  const laQuanTri = useLaQuanTriToChuc();
  const { tenantId } = useTenantCuaToi();
  const coTheXem = laQuanTri && Boolean(tenantId);
  const q = useHanMucTokenAi(coTheXem ? tenantId : undefined);
  const { t, locale } = useT();
  const dichLoi = useLoi();

  if (!coTheXem) return null;
  // Backend từ chối (vai trò đổi giữa phiên, tenant không còn): ẩn ô, không chiếm chỗ bằng một lỗi vô ích.
  if (maLoi(q.error) === 'PERMISSION_DENIED' || maLoi(q.error) === 'NOT_FOUND') return null;

  const d = q.data;
  const muc = d ? mucToken(d) : null;

  return (
    <Tile className="col-span-12">
      <TileHeader
        icon={<Sparkles size={16} className="text-content-muted" />}
        title={t('hanMucToken.toChucTieuDe')}
      />
      <div className="space-y-3 px-5 py-4">
        {q.isLoading ? (
          <div aria-busy="true" aria-label={t('hanMucToken.dangTai')} className="space-y-3">
            <Skeleton className="h-7 w-56 rounded" />
            <Skeleton className="h-2 w-full rounded-full" />
            <Skeleton className="h-4 w-64 rounded" />
          </div>
        ) : q.error || !d ? (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-3">
            <span className="min-w-0 text-sm text-danger-700">{t('hanMucToken.toChucLoi', { loi: dichLoi(q.error) })}</span>
            <button
              type="button"
              onClick={() => void q.refetch()}
              className="flex shrink-0 items-center gap-1.5 rounded-lg border border-default bg-surface px-3 py-1.5 text-xs font-medium text-content-secondary transition-colors hover:bg-subtle"
            >
              <RefreshCw size={13} />
              {t('chung.thuLai')}
            </button>
          </div>
        ) : (
          <>
            <TomTatTokenAi q={d} />
            <DongHanMuc q={d} doiTuong="to-chuc" />
            <CachTinhHanMuc q={d} doiTuong="to-chuc" />
            {muc === 'da-chan' && (
              <div role="alert" className="flex items-start gap-2 rounded border border-danger-200 bg-danger-50 px-3 py-2.5">
                <OctagonX size={16} className="mt-0.5 shrink-0 text-danger-600" />
                <div className="min-w-0 space-y-0.5 text-sm text-danger-700">
                  <p className="font-semibold">{t('hanMucToken.toChucDaChanTieuDe')}</p>
                  <p className="leading-relaxed">{t('hanMucToken.toChucDaChan', { ngay: ngayVN(d.resets_at) })}</p>
                </div>
              </div>
            )}
            {muc === 'canh-bao' && (
              <div role="status" className="flex items-start gap-2 rounded border border-warning-200 bg-warning-50 px-3 py-2.5">
                <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning-600" />
                <p className="min-w-0 text-sm leading-relaxed text-warning-700">
                  {t('hanMucToken.toChucCanhBao', { so: soVN(d.used_percent ?? 0, locale), ngay: ngayVN(d.resets_at) })}
                </p>
              </div>
            )}
            {tenantId && (
              <div className="border-t border-default pt-3">
                <ThuTuDungToken q={d} tenantId={tenantId} duocSua />
              </div>
            )}
          </>
        )}
        {tenantId && <YeuCauTokenToChuc tenantId={tenantId} />}
      </div>
    </Tile>
  );
}
